import React, { useEffect, useState } from "react";
import { StyledHistoryTechSkill } from "../TechSkillsHistory.styled";
import { DoubleStructure } from "../../../../../../../components/core/DoubleStructure/DoubleStructure";
import {
  AlignmentEnum,
  FontWeightEnum,
} from "../../../../../../../types/AppTypes";
import { BodyTyp } from "../../../../../../../components/Typography/Typography";
import SearchInput from "../../../../../../../components/core/SearchInput/SearchInput";
import {
  DESCRIPTION_LABEL,
  EMPTY_ATTACHMENTS_MESSAGE,
  EMPTY_STRING,
  SEARCH_LABEL,
  TECH_SKILL_LABEL,
  TECH_SKILLS_LABEL,
} from "../../../../../../../constants/Dictionary";
import { StyledExpandTable } from "../../../ProfessionalHistory.styled";
import {
  Table,
  TableThemeEnum,
} from "../../../../../../../components/core/Table/Table";
import { COLLABORATORS_PAGE_SIZE } from "../../../../../../../constants/AppConstants";
import { TextBox } from "../../../../../../../components/TextBoxGrid/TextBoxGrid.component";
import { getAttachTechSkillColumns } from "../../../../../../../constants/TableColumns";
import {
  Link,
  LinkSizeEnum,
} from "../../../../../../../components/core/Link/Link";
import { ReactComponent as AddIcon } from "../../../../../../../icons/add.svg";
import { useDebounce } from "use-debounce";
import { TechSkill } from "../../../../../../../types/api/techSkills.type";
import { useAppSelector } from "../../../../../../../hooks/store.hook";
import { CollaboratorDetails } from "../../../../../../../types/api/collaborators.type";
import { useGetAvailableTechSkills } from "../../../../../../../hooks/professionalHistory.hook";

export type AttachSelectableTechSkill = TechSkill & { level?: string };

type AttachHistoryTechSkillTableProps = {
  onAdd: () => void;
  setIsDirty: (isDirty: boolean) => void;
  selectedTechSkills: AttachSelectableTechSkill[];
  setSelectedTechSkills: React.Dispatch<
    React.SetStateAction<AttachSelectableTechSkill[]>
  >;
  collaborator: CollaboratorDetails;
};

export const AttachHistoryTechSkillTable = ({
  onAdd,
  setIsDirty,
  selectedTechSkills,
  setSelectedTechSkills,
  collaborator,
}: AttachHistoryTechSkillTableProps) => {
  const [searchValue, setSearchValue] = useState("");
  const [searchDebouncedValue] = useDebounce(searchValue, 500);

  const [pageNumber, setPageNumber] = useState(0);
  const { orgSettings } = useAppSelector((state) => state.config);
  const profileId = collaborator.id;

  const {
    data,
    isLoading: loading,
    isFetching,
  } = useGetAvailableTechSkills({
    profileId,
    pageNumber,
    searchDebouncedValue,
  });

  const handleRowSelect = (
    tech: AttachSelectableTechSkill,
    checked: boolean,
  ) => {
    setSelectedTechSkills((prev) => {
      if (checked) {
        return prev.some((i) => i.id === tech.id)
          ? prev
          : [
              ...prev,
              { ...tech, level: orgSettings.techSkills.levels[0].value },
            ];
      }
      return prev.filter((i) => i.id !== tech.id);
    });
  };

  const handleSelectAll = (
    rows: AttachSelectableTechSkill[],
    checked: boolean,
  ) => {
    if (!checked) {
      const pageIds = new Set(rows.map((r) => r.id));
      setSelectedTechSkills((prev) => prev.filter((c) => !pageIds.has(c.id)));
      return;
    }
    setSelectedTechSkills((prev) => {
      const existingIds = new Set(prev.map((c) => c.id));
      return [
        ...prev,
        ...rows
          .filter((r) => !existingIds.has(r.id))
          .map((r) => ({
            ...r,
            level: orgSettings.techSkills.levels[0].value,
          })),
      ];
    });
  };

  const onChangeLevel = (value: string | null, id: number) => {
    setSelectedTechSkills((prev) =>
      prev.map((skill) =>
        skill.id === id ? { ...skill, level: value ?? skill.level } : skill,
      ),
    );
  };

  const expandedRow = (record: AttachSelectableTechSkill) => {
    return (
      <TextBox
        label={DESCRIPTION_LABEL}
        text={record.description || EMPTY_STRING}
      />
    );
  };

  useEffect(() => {
    setIsDirty(selectedTechSkills.length > 0);
  }, [selectedTechSkills, setIsDirty]);

  useEffect(() => {
    setPageNumber(0);
  }, [searchDebouncedValue]);

  return (
    <StyledHistoryTechSkill>
      <div className="align-link-attach">
        <Link
          onClick={onAdd}
          icon={<AddIcon />}
          size={LinkSizeEnum.SMALL}
          disabled={loading || isFetching}
        >
          {TECH_SKILL_LABEL}
        </Link>
      </div>
      <DoubleStructure
        verticalAlignment={AlignmentEnum.CENTER}
        variant="primary"
      >
        <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
          {data &&
            `${data?.pagination.total} ${data?.data.length === 1 ? TECH_SKILL_LABEL : TECH_SKILLS_LABEL}`}
        </BodyTyp>

        <div className="filters">
          <SearchInput
            onChange={setSearchValue}
            label={`${SEARCH_LABEL}...`}
            name={"search"}
            value={searchValue}
            disabled={loading || isFetching}
          />
        </div>
      </DoubleStructure>
      <StyledExpandTable>
        <Table<AttachSelectableTechSkill>
          theme={TableThemeEnum.DARK}
          loading={loading || isFetching}
          rowKey={"id"}
          headers={getAttachTechSkillColumns(
            false,
            handleRowSelect,
            selectedTechSkills,
            orgSettings.techSkills.levels,
            onChangeLevel,
          )}
          scrollY={"36vh"}
          data={data?.data ?? []}
          emptyText={EMPTY_ATTACHMENTS_MESSAGE}
          pagination={{
            current: pageNumber + 1,
            pageSize: COLLABORATORS_PAGE_SIZE,
            total: data?.pagination.total ?? 0,
            onChange: (pageNumber) => setPageNumber(pageNumber - 1),
          }}
          expandable={{
            expandedRowRender: expandedRow,
          }}
          onCheckbox={handleRowSelect}
          onSelectAll={handleSelectAll}
          selectedRows={selectedTechSkills}
        />
      </StyledExpandTable>
    </StyledHistoryTechSkill>
  );
};
